import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import startupDilligenceImage from "../ai-agent/landing/_assets/startup-dilligence.png";
import customerReportImage from "./_assets/customer-report-image.jpg";
import customerIcon from "./_assets/customer-icon.svg";
import userIcon from "./_assets/user-icon.svg";

type SummaryType = {
    section: string;
    subsection: string;
    summary: string;
    stat_validation?: {
        valid?: string;
        confidence?: string;
        error_margin?: string;
        weighted_score?: string;
        comment?: string;
    };
    retrieved_sources?: {
        url: string;
        used: boolean;
        score: number;
        reason: string;
        query: string;
    }[];
};

type VisualType = {
    section: string;
    subsection: string;
    type: 'chart' | 'image';
    file?: string;
    image_url?: string;
};

const ReportViewer: React.FC = () => {
    const [summaryData, setSummaryData] = useState<SummaryType[]>([]);
    const [visualsMap, setVisualsMap] = useState<Record<string, VisualType[]>>({});
    const [company, setCompany] = useState('');
    const [reportType, setReportType] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        const loadFrontPage = async () => {
            try {
                const res = await fetch('/input/user_input.json');
                const data = await res.json();
                const companyName = (data.company || '').replace(/_/g, ' ').replace(/\b\w/g, (c: any) => c.toUpperCase());
                let useCase = (data.use_case || '').replace(/_/g, ' ').replace(/\b\w/g, (c: any) => c.toUpperCase());
                if (!useCase.includes('Report')) useCase += ' Report';

                setCompany(companyName);
                setReportType(useCase);
            } catch (e) {
                console.error('Error loading user input:', e);
            }
        };

        const loadReport = async () => {
            try {
                const summaryRes = await fetch('/output/statistical_analysis_detailed.json');

                if (!summaryRes.ok) throw new Error('Failed to load report file');

                let summary = await summaryRes.json();
                if (!Array.isArray(summary)) summary = [summary];
                setSummaryData(summary);

                // Optionally load visuals (commented out for now)
                // const visualRes = await fetch('/output/visuals_map.json');
                // const visuals: VisualType[] = await visualRes.json();
                // const map: Record<string, VisualType[]> = {};
                // visuals.forEach(v => {
                //   const key = `${v.section} > ${v.subsection}`;
                //   if (!map[key]) map[key] = [];
                //   map[key].push(v);
                // });
                // setVisualsMap(map);
            } catch (err: any) {
                setError(err.message);
            }
        };

        // loadFrontPage();
        loadReport();
    }, []);

    return (
        <div style={styles.body} className='w-full'>
            <div style={styles.container}>
                <div className='flex justify-between items-center mb-2'>
                    <div className='inline-flex gap-1 items-center'>
                        <img src={customerIcon} alt='' />
                        <span>Customer 360</span>
                    </div>
                    <div>
                        <div className='w-4 h-4 inline-flex justify-center items-center rounded-full bg-primary-900 text-white cursor-pointer'>
                            <img src={userIcon} alt='' />
                        </div>
                    </div>
                </div>
                <div style={styles.frontPage}>
                    <div style={styles.frontPageContent} className='relative overflow-hidden'>
                        <img src={startupDilligenceImage} alt="" className='absolute object-cover top-0 left-0 bottom-0 right-0 w-full h-full' />
                        <h1 className='relative bg-white text-lg p-1 rounded font-semibold'>Your Customer 360 Report is Ready. Every Insight is Traceable. Every Decision is Defensible</h1>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-5 relative  items-start">

                    <div className='md:sticky top-1' style={styles.toc}>
                        <h2 className='font-semibold text-2xl mb-2 border border-[#442873FF] rounded-lg p-1 mt-2 text-[#442873] flex justify-center'> <a href={`#customer-360`} style={styles.tocLink} className='!text-[#442873]'>Customer 360</a></h2>
                        <ul style={styles.tocList} className='p-0 m-0'>
                            {summaryData.map((s, idx) => {
                                const anchor = `${s.section}-${s.subsection}`.replace(/\s+/g, '-');
                                return (
                                    <li key={idx} style={styles.tocItem}>
                                        <a href={`#${anchor}`} style={styles.tocLink} title={`${s.section} ${s.subsection}`}>
                                            {s.section} &raquo; {s.subsection}
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>

                    <div className='md:col-span-3 flex flex-col scroll-smooth border border-[#D7D7D7] rounded-xl p-3'>
                        <div className='border-b border-[#D7D7D7] mb-2 pb-2 flex justify-between flex-col md:flex-row gap-2 md:items-center' id="customer-360">
                            <div>
                                <div className='text-lg font-semibold'>Customer 360 Intelligence Report</div>
                                <div className='text-base font-normal'>Customize sections to suit your needs.</div>
                            </div>
                            <div>
                                <a href="#Forecasting-Optimistic-Case:-Potential-LTV-uplift-with-targeted-personalization" className='border border-[#FFA300] bg-[#FFF7ED] px-5 py-1 text-[#373D3F] rounded-full' >Next</a>
                            </div>
                        </div>
                        <div className='flex-0'>
                            {error && <p style={{ color: 'red' }}>❌ Failed to load report data: {error}</p>}
                        </div>
                        <div className='overflow-y-auto flex-auto'>
                            <img src={customerReportImage} alt="" className='rounded-xl mb-2' />
                            {summaryData.map((s, idx) => {
                                const anchor = `${s.section}-${s.subsection}`.replace(/\s+/g, '-');
                                const visuals = visualsMap[`${s.section} > ${s.subsection}`] || [];
                                const usedSources = s.retrieved_sources?.filter(src => src.used) || [];
                                const unusedSources = s.retrieved_sources?.filter(src => !src.used) || [];

                                return (
                                    <div key={idx} className="section-block pt-2" id={anchor} style={styles.sectionBlock}>
                                        <h2 style={styles.h2} className='border border-[#442873FF] rounded-lg p-2 mt-2 mb-2'>{s.section} &raquo; {s.subsection}</h2>
                                        <div className="summary-text" style={styles.summaryText}>
                                            <ReactMarkdown>{s.summary}</ReactMarkdown>
                                        </div>

                                        <div className="score-metrics">
                                            <h4>📈 Statistical Validation</h4>
                                            <div><strong>Valid:</strong> {s.stat_validation?.valid ?? 'N/A'}</div>
                                            <div><strong>Confidence:</strong> {s.stat_validation?.confidence ?? 'N/A'}</div>
                                            <div><strong>Error Margin:</strong> {s.stat_validation?.error_margin ?? 'N/A'}</div>
                                            <div><strong>Weighted Score:</strong> {s.stat_validation?.weighted_score ?? 'N/A'}</div>
                                            <div><strong>Comment:</strong> {s.stat_validation?.comment ?? 'N/A'}</div>
                                        </div>

                                        {(usedSources.length > 0 || unusedSources.length > 0) && (
                                            <div className="sources-list" style={styles.sourcesList}>
                                                <h4>🔗 Sources</h4>
                                                {usedSources.length > 0 && (
                                                    <>
                                                        <h5>✅ Used Sources</h5>
                                                        <ul>{usedSources.map((src, i) => (
                                                            <li key={i}>
                                                                <a href={src.url} target="_blank" rel="noreferrer">{src.url}</a>
                                                                <small>
                                                                    🔍 Score: {src.score.toFixed(4)}<br />
                                                                    📝 Reason: {src.reason}<br />
                                                                    🔎 Query: {src.query}
                                                                </small>
                                                            </li>
                                                        ))}</ul>
                                                    </>
                                                )}
                                                {unusedSources.length > 0 && (
                                                    <>
                                                        <h5>❌ Not Used Sources</h5>
                                                        <ul>{unusedSources.map((src, i) => (
                                                            <li key={i}>
                                                                <a href={src.url} target="_blank" rel="noreferrer">{src.url}</a>
                                                                <small>
                                                                    🔍 Score: {src.score.toFixed(4)}<br />
                                                                    📝 Reason: {src.reason}<br />
                                                                    🔎 Query: {src.query}
                                                                </small>
                                                            </li>
                                                        ))}</ul>
                                                    </>
                                                )}
                                            </div>
                                        )}

                                        {visuals.map((vis, i) => (
                                            <div className="visual-wrapper" key={i} style={styles.visualWrapper}>
                                                <img
                                                    src={vis.type === 'chart' ? vis.file : vis.image_url}
                                                    alt={`Visual for ${s.section}-${s.subsection}`}
                                                    style={styles.visualImage}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                </div>

                <button style={styles.downloadBtn} onClick={() => window.print()}>
                    Download PDF
                </button>
            </div>
        </div>
    );
};

export default ReportViewer;

const styles: { [key: string]: React.CSSProperties } = {

    body: {
        fontFamily: `'Inter', sans-serif`,
        backgroundColor: '#f9f9fc',
        color: '#2b2b2b',
        margin: 0,
        padding: '0',
    },
    h2: {
        color: "#442873FF",
        fontSize: "20px",
        fontWeight: 600,
        // padding: "10px 0"
    },
    container: {
        maxWidth: '100%',
        margin: 'auto',
        background: '#fff',
        padding: '1rem 0',
        borderRadius: 0,
        boxShadow: 'none',
    },
    frontPage: {
        paddingBottom: '1rem',
    },
    frontPageContent: {
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        background: 'linear-gradient(to bottom right, #f8f9fa, #e9ecef)',
        borderRadius: 12,
        padding: '2rem',
    },
    toc: {
        background: '#FFF7ED',
        padding: '1rem',
        borderRadius: 10,
        marginBottom: '2rem',
    },
    tocList: {
        listStyle: 'decimal-leading-zero',
        paddingLeft: '2rem',
        textAlign: 'left',
    },
    tocItem: {
        marginBottom: '0.5rem',
    },
    tocLink: {
        textDecoration: 'none',
        color: '#373D3F',
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    sectionBlock: {

    },
    summaryText: {
        marginTop: '0.5rem',
        lineHeight: 1.6,
    },
    sourcesList: {
        marginTop: '1rem',
    },
    visualWrapper: {
        marginTop: '1rem',
        textAlign: 'center',
    },
    visualImage: {
        maxWidth: '100%',
        border: '1px solid #ccc',
        borderRadius: 8,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
    downloadBtn: {
        display: 'block',
        margin: '2rem auto 0',
        padding: '0.75rem 1.5rem',
        fontSize: '1rem',
        backgroundColor: '#1a73e8',
        color: 'white',
        border: 'none',
        borderRadius: 8,
        cursor: 'pointer',
    },
};
